// Viewer-side receive cap; see docs/qualidade-recebida.md.

/** `0` on an axis means no cap on that axis. */
export interface ReceiveLimit {
    /** Short side in pixels (the "720" in 720p). */
    maxShortSide: number
    maxFps: number
}

export type ReceivePreference = 'auto' | 'source' | '720' | '480' | '360'

export interface ReceivePreset {
    label: string
    description: string
    /** `null` for `auto` (comes from the tile size) and `source` (no cap). */
    limit: ReceiveLimit | null
}

export const RECEIVE_PRESETS: Record<ReceivePreference, ReceivePreset> = {
    auto: {
        label: 'Automática',
        description: 'Resolução pelo tamanho de cada tela no seu monitor, fps de quem transmite',
        limit: null
    },
    source: {
        label: 'Original',
        description: 'O que a pessoa transmite, sem economia',
        limit: null
    },
    720: {
        label: 'Até 720p',
        description: '720p a 30 fps',
        limit: { maxShortSide: 720, maxFps: 30 }
    },
    480: {
        label: 'Até 480p',
        description: '480p a 30 fps',
        limit: { maxShortSide: 480, maxFps: 30 }
    },
    360: {
        label: 'Economia',
        description: '360p a 15 fps, pra internet fraca',
        limit: { maxShortSide: 360, maxFps: 15 }
    }
}

export const RECEIVE_ORDER: ReceivePreference[] = ['auto', 'source', '720', '480', '360']
export const DEFAULT_RECEIVE: ReceivePreference = 'auto'

const RECEIVE_KEY = 'screen-share:receive-quality'

export function loadReceivePreference(): ReceivePreference {
    try {
        const saved = localStorage.getItem(RECEIVE_KEY)
        return saved && saved in RECEIVE_PRESETS ? saved as ReceivePreference : DEFAULT_RECEIVE
    } catch {
        return DEFAULT_RECEIVE
    }
}

export function saveReceivePreference(preference: ReceivePreference): void {
    try {
        localStorage.setItem(RECEIVE_KEY, preference)
    } catch {
        // Private window: the choice just lasts for this visit.
    }
}

/**
 * Steps the auto mode asks for; anything above the last one gets the source.
 * Size only: a small tile still gets the sender's fps, so every viewer sees the same motion.
 */
const AUTO_STEPS = [360, 540, 720]
/** Leeway so a 380px tile doesn't jump to 540p over 20 pixels. */
const AUTO_SLACK = 1.1

/** Short side of the picture as drawn in the tile (object-contain), in device pixels. */
export function displayedShortSide(
    tile: { width: number; height: number },
    video: { width: number; height: number },
    pixelRatio: number,
    zoom = 1
): number {
    if (tile.width <= 0 || tile.height <= 0) return 0
    const aspect = video.width > 0 && video.height > 0 ? video.width / video.height : 16 / 9
    const width = Math.min(tile.width, tile.height * aspect)
    const height = width / aspect
    return Math.round(Math.min(width, height) * Math.max(1, pixelRatio) * Math.max(1, zoom))
}

/** `null` means the tile is big enough for the source. */
export function autoLimit(shortSide: number): ReceiveLimit | null {
    if (shortSide <= 0) return null
    const step = AUTO_STEPS.find((candidate) => shortSide <= candidate * AUTO_SLACK)
    return step ? { maxShortSide: step, maxFps: 0 } : null
}

/** A fixed choice still shrinks for small tiles: the tighter cap wins. */
export function receiveLimit(preference: ReceivePreference, shortSide: number): ReceiveLimit | null {
    if (preference === 'source') return null
    const auto = autoLimit(shortSide)
    const fixed = RECEIVE_PRESETS[preference].limit
    if (preference === 'auto' || !fixed) return auto
    if (!auto) return fixed
    return {
        maxShortSide: tighter(fixed.maxShortSide, auto.maxShortSide),
        maxFps: tighter(fixed.maxFps, auto.maxFps)
    }
}

/** The smaller cap, where `0` is no cap rather than the smallest. */
function tighter(a: number, b: number): number {
    return a && b ? Math.min(a, b) : a || b
}

/** Several tiles showing one stream: send what the largest one needs. */
export function loosestLimit(limits: (ReceiveLimit | null)[]): ReceiveLimit | null {
    if (limits.length === 0 || limits.some((limit) => !limit)) return null
    const caps = limits as ReceiveLimit[]
    const widest = (pick: (limit: ReceiveLimit) => number) => (
        caps.some((limit) => pick(limit) === 0) ? 0 : Math.max(...caps.map(pick))
    )
    return { maxShortSide: widest((limit) => limit.maxShortSide), maxFps: widest((limit) => limit.maxFps) }
}

/** Comes from another browser, so clamp it. */
export function sanitizeLimit(raw: unknown): ReceiveLimit | null {
    if (!raw || typeof raw !== 'object') return null
    const { maxShortSide, maxFps } = raw as Record<string, unknown>
    const side = typeof maxShortSide === 'number' && Number.isFinite(maxShortSide) ? Math.round(maxShortSide) : 0
    const fps = typeof maxFps === 'number' && Number.isFinite(maxFps) ? Math.round(maxFps) : 0
    const limit = {
        maxShortSide: side > 0 ? Math.min(4320, Math.max(144, side)) : 0,
        maxFps: fps > 0 ? Math.min(120, Math.max(5, fps)) : 0
    }
    return limit.maxShortSide || limit.maxFps ? limit : null
}

/** Scales with pixels per second from 1080p60 = 10 Mbps, enough for readable screen text. */
export function bitrateFor(limit: ReceiveLimit): number {
    if (!limit.maxShortSide) return 0
    const pixels = (limit.maxShortSide / 1080) ** 2
    const cadence = limit.maxFps ? Math.min(1, limit.maxFps / 60) : 1
    return Math.max(250_000, Math.round(10_000_000 * pixels * cadence))
}

/** Applied by the sender on that viewer's copy only. */
export interface LimitEncoding {
    scaleResolutionDownBy: number
    maxFramerate?: number
    maxBitrate?: number
}

export function encodingFor(
    limit: ReceiveLimit | null,
    source: { width?: number; height?: number }
): LimitEncoding {
    if (!limit) return { scaleResolutionDownBy: 1 }
    const sourceShort = Math.min(source.width ?? 0, source.height ?? 0)
    const scale = limit.maxShortSide && sourceShort > limit.maxShortSide
        ? sourceShort / limit.maxShortSide
        : 1
    return {
        scaleResolutionDownBy: Math.round(scale * 100) / 100,
        ...(limit.maxFps ? { maxFramerate: limit.maxFps } : {}),
        ...(limit.maxShortSide ? { maxBitrate: bitrateFor(limit) } : {})
    }
}

/** Minimum jitter buffer for incoming video; trades ~100 ms of delay for no stutter on jittery links. */
export const PLAYOUT_TARGET_MS = 100

export function smoothPlayout(receiver: RTCRtpReceiver | undefined): void {
    if (!receiver) return
    const target = receiver as { jitterBufferTarget?: number | null; playoutDelayHint?: number }
    try {
        if ('jitterBufferTarget' in receiver) target.jitterBufferTarget = PLAYOUT_TARGET_MS
        else if ('playoutDelayHint' in receiver) target.playoutDelayHint = PLAYOUT_TARGET_MS / 1000
    } catch {
        // Older engines reject the value; playback just keeps the default buffer.
    }
}
